import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { canViewAdminSettings } from '../../permissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Platform-wide theme controls. Today just Memorial Day, but structured so
// additional seasonal themes (St. Patrick's, July 4th, Christmas, etc.) can
// plug in with the same three-state model: hidden / available / applied.
export default function ThemesAdminPage() {
  const { user, getHeaders } = useAuth();
  const [memorialState, setMemorialState] = useState({ state: 'hidden', appliedAt: null });
  const [loading, setLoading] = useState(true);

  const loadMemorialState = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/public-settings/memorial-theme`);
      if (r.ok) {
        const data = await r.json();
        setMemorialState(data);
        window.__memorialThemeState = data;
      }
    } catch (err) { /* leave default */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canViewAdminSettings(user)) loadMemorialState();
    else setLoading(false);
  }, [user, loadMemorialState]);

  async function setMemorialThemePlatformState(newState) {
    try {
      await fetch(`${API_BASE}/settings/memorial_day_theme_state`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ value: newState, isSecret: false }),
      });
      if (newState === 'applied') {
        await fetch(`${API_BASE}/settings/memorial_day_theme_applied_at`, {
          method: 'PUT', headers: getHeaders(),
          body: JSON.stringify({ value: new Date().toISOString(), isSecret: false }),
        });
      }
      await loadMemorialState();
      window.dispatchEvent(new Event('theme-change'));
    } catch (err) {
      alert('Failed to update theme state: ' + err.message);
    }
  }

  if (!canViewAdminSettings(user)) {
    return (
      <div className="page-content">
        <div className="empty-state"><h3>Admin only</h3><p>Theme admin is admin-only.</p></div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>Themes</h2>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: '760px' }}>
        {loading && <div style={{ padding: '20px', color: '#777' }}>Loading...</div>}

        <ThemeAdminCard
          title="Memorial Day"
          description="Controls visibility and default state of the Memorial Day skin for every user."
          state={memorialState.state}
          appliedAt={memorialState.appliedAt}
          onChange={setMemorialThemePlatformState}
          activeColor="#B22234"
        />

        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Additional Themes</h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '13px', color: '#777', margin: 0, lineHeight: 1.5 }}>
              No other platform-wide themes are wired up yet. When adding one (e.g. July 4th, Christmas), follow the Memorial Day pattern: add a <code>*_theme_state</code> and <code>*_theme_applied_at</code> key pair to <code>precon.Settings</code>, surface it on <code>/api/public-settings/</code>, and add a corresponding card above.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function ThemeAdminCard({ title, description, state, appliedAt, onChange, activeColor }) {
  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header">
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title} Theme</h3>
      </div>
      <div className="card-body">
        <div style={{ fontSize: '12px', color: '#777', marginBottom: '12px', lineHeight: 1.5 }}>
          {description} <strong>Hidden</strong> removes it from theme pickers. <strong>Available</strong> lets users opt in from their own Settings. <strong>Apply to everyone</strong> force-pushes it as the active theme for all users; individuals can still override from their own Settings afterward.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {[
            { id: 'hidden',    label: 'Hidden',            desc: 'Off for everyone' },
            { id: 'available', label: 'Available',         desc: 'Opt-in per user' },
            { id: 'applied',   label: 'Apply to Everyone', desc: 'Default for all' },
          ].map(opt => {
            const active = state === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => onChange(opt.id)}
                style={{
                  padding: '12px 10px', borderRadius: '8px',
                  border: active ? `2px solid ${activeColor}` : '2px solid var(--color-gray-200)',
                  background: active ? `${activeColor}0f` : 'var(--color-white)',
                  textAlign: 'center', cursor: 'pointer',
                  fontSize: '13px', fontWeight: active ? 600 : 500,
                  color: active ? activeColor : 'var(--color-gray-900)',
                  transition: 'all 0.15s',
                }}
              >
                <div>{opt.label}</div>
                <div style={{ fontSize: '10px', color: '#999', marginTop: '3px', fontWeight: 400 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
        {state === 'applied' && appliedAt && (
          <div style={{ marginTop: '10px', fontSize: '11px', color: '#777' }}>
            Pushed to everyone on {new Date(appliedAt).toLocaleString()}. Users who pick a different theme after this time will keep their choice.
          </div>
        )}
      </div>
    </div>
  );
}
